import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { showSuccessToast, showErrorToast } from "@/utils/toast-utils";

interface SendInvitationsParams {
  testId: string;
  emails: string[];
}

export const useTestInvitations = (testId: string) => {
  const queryClient = useQueryClient();

  // Fetch invitations for a test
  const { data: invitations, isLoading } = useQuery({
    queryKey: ["test-invitations", testId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_invitations")
        .select("*")
        .eq("test_id", testId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!testId,
  });

  // Send invitations
  const sendInvitations = useMutation({
    mutationFn: async ({ testId, emails }: SendInvitationsParams) => {
      console.log('[sendInvitations] Invoking edge function with:', { testId, emails });
      
      const { data, error } = await supabase.functions.invoke("send-test-invitation", {
        body: { testId, emails },
      });

      console.log('[sendInvitations] Response:', { data, error });

      if (error) {
        console.error('[sendInvitations] Edge function error:', error);
        throw error;
      }

      // Validate response structure
      if (!data || typeof data.successCount === 'undefined') {
        console.error('[sendInvitations] Invalid response structure:', data);
        throw new Error('Invalid response from invitation service');
      }

      return data;
    },
    onSuccess: (data) => {
      const { successCount, failureCount, failedEmails } = data;

      if (successCount > 0) {
        showSuccessToast({
          title: "Invitations sent",
          description: `Successfully sent ${successCount} invitation${successCount > 1 ? "s" : ""}${
            failureCount > 0 ? `, ${failureCount} failed` : ""
          }`,
        });
      }

      if (failureCount > 0 && failedEmails?.length > 0) {
        showErrorToast({
          title: "Some invitations failed",
          description: `Failed to send to: ${failedEmails.join(", ")}`,
        });
      }

      // Refresh invitations list
      queryClient.invalidateQueries({ queryKey: ["test-invitations", testId] });
    },
    onError: (error: any) => {
      console.error("[sendInvitations] Error:", error);
      
      let errorMessage = "An unexpected error occurred";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }
      
      showErrorToast({
        title: "Failed to send invitations",
        description: errorMessage,
      });
    },
  });

  // Delete invitation
  const deleteInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("test_invitations")
        .delete()
        .eq("id", invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      showSuccessToast({
        title: "Invitation removed",
        description: "The invitation has been removed",
      });
      queryClient.invalidateQueries({ queryKey: ["test-invitations", testId] });
    },
    onError: (error: any) => {
      console.error("Error deleting invitation:", error);
      showErrorToast({
        title: "Failed to remove invitation",
        description: error.message || "An unexpected error occurred",
      });
    },
  });

  return {
    invitations,
    isLoading,
    sendInvitations,
    isSending: sendInvitations.isPending,
    deleteInvitation,
    isDeleting: deleteInvitation.isPending,
  };
};
